import { create } from 'zustand'
import type {
  ConversationDetailResponse,
  ConversationFilters,
  ConversationListEntry,
  Message,
  MessageApproval,
} from '../modules/conversations/types.js'

export interface TabEntry {
  id: string
  displayName: string
  channelOrigin: string
}

interface ConversationsStoreState {
  filters: ConversationFilters
  items: ConversationListEntry[]
  nextOffset: number | null
  totalCount: number | null
  ratingScale: number
  selectedId: string | null
  selectedTransferredOut: boolean
  loading: boolean
  error: string | null
  messagesByConversationId: Record<string, Message[]>
  failureReasonsByMessageId: Record<string, string>
  messageApprovalsByMessageId: Record<string, MessageApproval>
  // UI-1f Task 9: per-conversation co-agent ids. Seeded from the detail
  // fetch; mutated by the co-agent socket events so CoAgentDialog re-renders.
  coAgentIdsByConversationId: Record<string, string[]>
  conversationById: Record<string, ConversationDetailResponse['conversation']>
  openTabs: TabEntry[]

  page: number
  setPage: (page: number) => void

  setConversationDetail: (conversation: ConversationDetailResponse['conversation']) => void
  setCoAgentIds: (conversationId: string, ids: string[]) => void
  addCoAgent: (conversationId: string, agentId: string) => void
  removeCoAgent: (conversationId: string, agentId: string) => void

  setMessages: (conversationId: string, messages: Message[]) => void
  prependMessages: (conversationId: string, messages: Message[]) => void
  appendMessage: (conversationId: string, message: Message) => void
  replaceMessage: (conversationId: string, oldId: string, newMessage: Message) => void
  patchMessage: (
    conversationId: string,
    messageId: string,
    patch: Partial<Message>,
  ) => void
  markMessageFailed: (
    conversationId: string,
    messageId: string,
    error: string,
  ) => void
  setFailureReason: (messageId: string, reason: string) => void
  clearFailureReason: (messageId: string) => void
  setMessageApprovals: (approvals: MessageApproval[]) => void
  upsertMessageApproval: (approval: MessageApproval) => void
  clearMessageApproval: (messageId: string) => void

  setFilters: (partial: Partial<ConversationFilters>) => void
  setList: (
    items: ConversationListEntry[],
    nextOffset: number | null,
    totalCount: number | null,
  ) => void
  appendItems: (items: ConversationListEntry[], nextOffset: number | null) => void
  setRatingScale: (scale: number) => void
  patchListItem: (id: string, patch: Partial<ConversationListEntry>) => void
  reorderToTop: (id: string) => void
  removeListItem: (id: string) => void
  prependItem: (item: ConversationListEntry) => void
  setSelectedId: (id: string | null) => void
  openTab: (entry: TabEntry) => void
  closeTab: (id: string) => void
  setSelectedTransferredOut: (transferredOut: boolean) => void
  clearSelectedTransferredOut: () => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  reset: () => void
}

const initialFilters: ConversationFilters = {
  status: 'open',
  channels: [],
  assignee: 'me-and-queues',
  searchQuery: '',
  limit: 50,
}

const initialState = {
  filters: initialFilters,
  items: [] as ConversationListEntry[],
  nextOffset: null as number | null,
  totalCount: null as number | null,
  ratingScale: 5,
  selectedId: null as string | null,
  selectedTransferredOut: false,
  loading: false,
  error: null as string | null,
  messagesByConversationId: {} as Record<string, Message[]>,
  failureReasonsByMessageId: {} as Record<string, string>,
  messageApprovalsByMessageId: {} as Record<string, MessageApproval>,
  coAgentIdsByConversationId: {} as Record<string, string[]>,
  conversationById: {} as Record<string, ConversationDetailResponse['conversation']>,
  openTabs: [] as TabEntry[],
  page: 1,
}

export const useConversationsStore = create<ConversationsStoreState>()((set) => ({
  ...initialState,
  setFilters: (partial) => set((s) => ({ filters: { ...s.filters, ...partial }, page: 1 })),
  setPage: (page) => set({ page }),
  setList: (items, nextOffset, totalCount) => set({ items, nextOffset, totalCount }),
  appendItems: (items, nextOffset) =>
    set((s) => ({ items: [...s.items, ...items], nextOffset })),
  setRatingScale: (ratingScale) => set({ ratingScale }),
  patchListItem: (id, patch) =>
    set((s) => ({
      items: s.items.map((i) => (i.id === id ? { ...i, ...patch } : i)),
    })),
  reorderToTop: (id) =>
    set((s) => {
      const idx = s.items.findIndex((i) => i.id === id)
      if (idx <= 0) return s
      const item = s.items[idx]!
      return { items: [item, ...s.items.slice(0, idx), ...s.items.slice(idx + 1)] }
    }),
  removeListItem: (id) =>
    set((s) => ({ items: s.items.filter((i) => i.id !== id) })),
  prependItem: (item) =>
    set((s) => ({ items: [item, ...s.items.filter((i) => i.id !== item.id)] })),
  setSelectedId: (selectedId) => set({ selectedId }),
  openTab: (entry: TabEntry) =>
    set((s) => {
      const exists = s.openTabs.find((t) => t.id === entry.id)
      const tabs = exists ? s.openTabs : [...s.openTabs, entry]
      return { openTabs: tabs, selectedId: entry.id }
    }),
  closeTab: (id: string) =>
    set((s) => {
      const tabs = s.openTabs.filter((t) => t.id !== id)
      const nextSelected: string | null =
        s.selectedId === id ? (tabs.length > 0 ? tabs[tabs.length - 1]!.id : null) : s.selectedId
      return { openTabs: tabs, selectedId: nextSelected }
    }),
  setSelectedTransferredOut: (selectedTransferredOut) => set({ selectedTransferredOut }),
  clearSelectedTransferredOut: () => set({ selectedTransferredOut: false }),
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),
  setMessages: (conversationId, messages) =>
    set((s) => ({
      messagesByConversationId: {
        ...s.messagesByConversationId,
        [conversationId]: messages,
      },
    })),
  prependMessages: (conversationId, messages) =>
    set((s) => {
      const list = s.messagesByConversationId[conversationId] ?? []
      const combined = [...messages, ...list]
      const seen = new Set<string>()
      const next: Message[] = []
      for (const m of combined) {
        if (seen.has(m.id)) continue
        seen.add(m.id)
        next.push(m)
      }
      return { messagesByConversationId: { ...s.messagesByConversationId, [conversationId]: next } }
    }),
  appendMessage: (conversationId, message) =>
    set((s) => {
      const list = s.messagesByConversationId[conversationId] ?? []
      if (list.some((m) => m.id === message.id)) return s
      return {
        messagesByConversationId: {
          ...s.messagesByConversationId,
          [conversationId]: [...list, message],
        },
      }
    }),
  replaceMessage: (conversationId, oldId, newMessage) =>
    set((s) => {
      const list = s.messagesByConversationId[conversationId] ?? []
      return {
        messagesByConversationId: {
          ...s.messagesByConversationId,
          [conversationId]: list.map((m) => (m.id === oldId ? newMessage : m)),
        },
      }
    }),
  patchMessage: (conversationId, messageId, patch) =>
    set((s) => {
      const list = s.messagesByConversationId[conversationId] ?? []
      return {
        messagesByConversationId: {
          ...s.messagesByConversationId,
          [conversationId]: list.map((m) =>
            m.id === messageId ? { ...m, ...patch } : m,
          ),
        },
      }
    }),
  markMessageFailed: (conversationId, messageId, _error) =>
    set((s) => {
      const list = s.messagesByConversationId[conversationId] ?? []
      return {
        messagesByConversationId: {
          ...s.messagesByConversationId,
          [conversationId]: list.map((m) =>
            m.id === messageId ? { ...m, status: 'failed' } : m,
          ),
        },
      }
    }),
  setFailureReason: (messageId, reason) =>
    set((s) => ({
      failureReasonsByMessageId: {
        ...s.failureReasonsByMessageId,
        [messageId]: reason,
      },
    })),
  clearFailureReason: (messageId) =>
    set((s) => {
      const next = { ...s.failureReasonsByMessageId }
      delete next[messageId]
      return { failureReasonsByMessageId: next }
    }),
  setMessageApprovals: (approvals) =>
    set((s) => {
      const next = { ...s.messageApprovalsByMessageId }
      for (const a of approvals) next[a.messageId] = a
      return { messageApprovalsByMessageId: next }
    }),
  upsertMessageApproval: (approval) =>
    set((s) => ({
      messageApprovalsByMessageId: {
        ...s.messageApprovalsByMessageId,
        [approval.messageId]: approval,
      },
    })),
  clearMessageApproval: (messageId) =>
    set((s) => {
      const next = { ...s.messageApprovalsByMessageId }
      delete next[messageId]
      return { messageApprovalsByMessageId: next }
    }),
  setConversationDetail: (conversation) =>
    set((s) => ({
      conversationById: {
        ...s.conversationById,
        [conversation.id]: conversation,
      },
    })),
  setCoAgentIds: (conversationId, ids) =>
    set((s) => ({
      coAgentIdsByConversationId: {
        ...s.coAgentIdsByConversationId,
        [conversationId]: ids,
      },
    })),
  addCoAgent: (conversationId, agentId) =>
    set((s) => {
      const cur = s.coAgentIdsByConversationId[conversationId]
      // Cross-conversation safety: only mutate a conversation we know about.
      if (cur === undefined) return s
      if (cur.includes(agentId)) return s
      return {
        coAgentIdsByConversationId: {
          ...s.coAgentIdsByConversationId,
          [conversationId]: [...cur, agentId],
        },
      }
    }),
  removeCoAgent: (conversationId, agentId) =>
    set((s) => {
      const cur = s.coAgentIdsByConversationId[conversationId]
      if (cur === undefined) return s
      return {
        coAgentIdsByConversationId: {
          ...s.coAgentIdsByConversationId,
          [conversationId]: cur.filter((id) => id !== agentId),
        },
      }
    }),
  reset: () =>
    set({
      ...initialState,
      filters: { ...initialFilters },
      messagesByConversationId: {},
      failureReasonsByMessageId: {},
      messageApprovalsByMessageId: {},
      coAgentIdsByConversationId: {},
      conversationById: {},
    }),
}))
